export type HotamApiErrorBody = {
  code?: string;
  message?: string;
  statusCode?: number;
};

export class HotamApiError extends Error {
  readonly body: HotamApiErrorBody | null;

  readonly status: number;

  constructor(response: Response, body: HotamApiErrorBody | null) {
    super(body?.message ?? `Hotam API responded with ${response.status}`);
    this.name = 'HotamApiError';
    this.body = body;
    this.status = response.status;
  }
}

export type HotamClientOptions = {
  baseUrl: string | (() => string);
  fetcher?: typeof fetch;
  getAccessToken?: () => string | null | Promise<string | null>;
  onUnauthorized?: () => void | Promise<void>;
};

export type HotamRequestOptions = RequestInit & {
  authenticated?: boolean;
};

export type HotamClient = {
  request<T>(path: string, options?: HotamRequestOptions): Promise<T>;
};

type SuccessResponse<T> = {
  data: T;
  resultCode: number;
};

const isSuccessResponse = <T>(payload: unknown): payload is SuccessResponse<T> =>
  typeof payload === 'object' && payload !== null && 'data' in payload && 'resultCode' in payload;

const parseResponse = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

export const createHotamClient = ({
  baseUrl,
  fetcher = fetch,
  getAccessToken,
  onUnauthorized,
}: HotamClientOptions): HotamClient => {
  const getBaseUrl = () => (typeof baseUrl === 'function' ? baseUrl() : baseUrl).replace(/\/$/, '');

  return {
    async request<T>(path: string, options: HotamRequestOptions = {}) {
      const { authenticated = false, ...requestInit } = options;
      const headers = new Headers(requestInit.headers);
      headers.set('Accept', 'application/json');

      if (requestInit.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      if (authenticated) {
        const accessToken = await getAccessToken?.();
        if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
      }

      const response = await fetcher(`${getBaseUrl()}/${path.replace(/^\//, '')}`, {
        ...requestInit,
        headers,
      });
      const payload = await parseResponse(response);

      if (!response.ok) {
        if (response.status === 401) await onUnauthorized?.();
        const body = typeof payload === 'object' ? (payload as HotamApiErrorBody) : null;
        throw new HotamApiError(response, body);
      }

      return isSuccessResponse<T>(payload) ? payload.data : (payload as T);
    },
  };
};
